import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { RippleModule } from 'primeng/ripple';
import { SidebarModule } from 'primeng/sidebar';
import { StyleClassModule } from 'primeng/styleclass';
import { MegaMenuItem } from 'primeng/api';
import { MegaMenuModule } from 'primeng/megamenu';
import { AdminPanelService } from '../../services/admin-panel.service';
import { getAdminChildRoutes } from '../../config';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

@Component({
  selector: 'app-admin-menu',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PanelModule,
    ButtonModule,
    SidebarModule,
    RippleModule,
    StyleClassModule,
    MegaMenuModule
],
  templateUrl: './admin-menu.component.html',
  styleUrl: './admin-menu.component.scss'
})
export class AdminMenuComponent implements OnInit, OnDestroy {
    public items: MegaMenuItem[] | undefined;
    private destroy$ = new Subject<void>();
    private currentUrl: string = '';

    // Track timeouts for cleanup
    private timeouts: number[] = [];

    constructor(
        private adminPanelService: AdminPanelService,
        private router: Router
    ) { }

    ngOnInit() {
        this.loadMenu();
        this.subscribeToRouterEvents();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();

        // Clear all pending timeouts
        this.timeouts.forEach(id => clearTimeout(id));
        this.timeouts = [];
    }

    private subscribeToRouterEvents() {
        // Subscribe to router events to update active menu item
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe((event: NavigationEnd) => {
                this.currentUrl = event.url;
                this.updateActiveMenuItem();
            });

        // Set initial active state
        this.currentUrl = this.router.url;
        this.updateActiveMenuItem();
    }

    public loadMenu() {
        const adminChildRoutes = getAdminChildRoutes();
        this.items = this.adminPanelService.getMenuItemsFromRouteConfig(adminChildRoutes);
        // Update active state after menu is loaded
        const timeoutId = window.setTimeout(() => this.updateActiveMenuItem(), 0);
        this.timeouts.push(timeoutId);
    }

    private updateActiveMenuItem() {
        if (!this.items) return;

        this.items.forEach(item => {
            // Reset all items
            item.styleClass = 'admin-menu-item';

            // Check if this item should be active
            if (this.isMenuItemActive(item)) {
                item.styleClass = 'admin-menu-item active-menu-item';
            }
        });
    }

    private isMenuItemActive(item: MegaMenuItem): boolean {
        if (!item.routerLink) return false;

        // Convert routerLink to string
        const itemPath = Array.isArray(item.routerLink) ? item.routerLink.join('/') : item.routerLink;

        // Clean up URLs (remove trailing slashes)
        const cleanCurrentUrl = this.currentUrl.replace(/\/$/, '') || '/admin/panel';
        const cleanItemPath = itemPath.replace(/\/$/, '');
        // Handle exact match for dashboard (root route)
        if (cleanItemPath === '/admin/panel') {
            return cleanCurrentUrl === '/admin/panel';
        }

        // Handle other routes - check if current URL starts with the item path
        return cleanCurrentUrl === cleanItemPath || cleanCurrentUrl.startsWith(cleanItemPath + '/');
    }

    /**
     * Optional: Add custom menu items that are not part of the routing configuration
     */
    private addCustomMenuItems() {
        if (this.items) {
            // Example: Add a logout or settings item
            this.items.push({
                label: 'Settings',
                icon: 'pi pi-cog',
                routerLink: ['/admin/panel/settings'],
                routerLinkActiveOptions: false,
                styleClass: 'admin-menu-item'
            });
        }
    }

}
